import { FC } from 'react';
import { useTranslations } from 'next-intl';
import Price from 'components/price';
import { PurifiedHtml } from 'components/purified-html';
import CommonAvailabilityWrapper from './common-availability-wrapper';
import { CatCard, CatHeading, CatTextLink } from 'components/blocks-components';
import AddToCart from 'components/actions/add-to-cart/add-to-cart';
import { DealerData } from 'types/parts-diagram';
import { DealerResponse } from 'services/dealer';
import { ReplacementParts } from 'services/dealerPriceAvailability/types';
import ContactYourDealerPopover from 'components/contact-your-dealer/contact-your-dealer-popover/contact-your-dealer-popover';
import styles from './reman-parts/reman-parts.module.scss';
import cx from 'classnames';
import { Conditional } from '@cat-ecom/pcc-components';
import ProductReusableCard from './product-reusable-card/product-reusable-card';

interface PdpPartsMobileViewProps {
  remanItem;
  locale: string;
  dealerData: DealerData;
  dealerInformation: DealerResponse;
  availRemoveInterimStockFlag: boolean;
  isShowSourcingDetailEnabled: boolean;
  isDeliveryEnabled: boolean;
  currency: string;
  catcorp: boolean;
  replacementParts?: ReplacementParts[];
  setDrawerActive?: (drawerActive: boolean) => void;
  handleOpen?: (partNumber: string, price: unknown) => Promise<void>;
  isPccReleaseRemanReplacement?: boolean;
  enableAddToCart?: boolean;
  remanPart?: boolean;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}

const PdpPartsMobileView: FC<PdpPartsMobileViewProps> = ({
  remanItem,
  locale,
  dealerData,
  dealerInformation,
  availRemoveInterimStockFlag,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  currency,
  catcorp,
  replacementParts = [],
  setDrawerActive,
  handleOpen,
  isPccReleaseRemanReplacement,
  enableAddToCart,
  remanPart,
  isRTL = false,
  currencyLdFeatureFlag = false
}) => {
  const t = useTranslations();

  const renderUpdatedReplacementPartForMobile = () => {
    return (
      <div className={cx(styles['item-container'])}>
        <CatCard>
          <ProductReusableCard
            remanItem={remanItem}
            locale={locale}
            dealerData={dealerData}
            dealerInformation={dealerInformation}
            availRemoveInterimStockFlag={availRemoveInterimStockFlag}
            isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
            isDeliveryEnabled={isDeliveryEnabled}
            currency={currency}
            replacementParts={replacementParts}
            setDrawerActive={setDrawerActive}
            handleOpen={handleOpen}
            enableAddToCart={enableAddToCart}
            catcorp={catcorp}
            isPccReleaseRemanReplacement={isPccReleaseRemanReplacement}
            isRTL={isRTL}
            currencyLdFeatureFlag={currencyLdFeatureFlag}
          />
        </CatCard>
      </div>
    );
  };

  return (
    <Conditional
      test={!isPccReleaseRemanReplacement}
      fallback={renderUpdatedReplacementPartForMobile()}
    >
      <div className="cat-u-margin-bottom-sm">
        <div className="w-100 border-top" />
        <div className="my-2">
          <CatTextLink
            onClick={() => {
              setDrawerActive(true);
              handleOpen(remanItem.partNumber, remanItem);
            }}
          >
            <PurifiedHtml
              htmlString={`${remanItem.partNumber} : ${remanItem.name}`}
            />
          </CatTextLink>
          <div className="cat-u-margin-top-sm">
            {replacementParts.length > 0 ? (
              <CatHeading variant="label">
                {t('QUANTITY1')}: {remanItem.quantity}
              </CatHeading>
            ) : null}
          </div>
        </div>
        <div className="d-flex justify-content-between my-2">
          <div className="d-flex flex-column ">
            {!catcorp && (
              <CommonAvailabilityWrapper
                availabilityData={remanItem}
                shouldDisplayLabel={false}
                isLoading={false}
                locale={locale}
                dealerData={dealerData}
                dealerInformation={dealerInformation}
                availRemoveInterimStockFlag={availRemoveInterimStockFlag}
                isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
                isDeliveryEnabled={isDeliveryEnabled}
              />
            )}
          </div>
          <div className="text-end">
            {!catcorp && (
              <Price
                price={remanItem}
                currency={currency}
                isCoreDepositMobileDrawer
                isRTL={isRTL}
                currencyLdFeatureFlag={currencyLdFeatureFlag}
              />
            )}
          </div>
        </div>
        {!catcorp && (
          <Conditional
            test={enableAddToCart}
            fallback={
              <ContactYourDealerPopover
                dealerAddress={dealerData?.address}
                responsiveBehavior
                fullWidth={false}
              />
            }
          >
            <AddToCart productPrice={remanItem} fullWidth={false} />
          </Conditional>
        )}
      </div>
    </Conditional>
  );
};

export default PdpPartsMobileView;
